import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Download, FileSpreadsheet, FileText, File, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Export Dropdown Button Component
 * Provides CSV, PDF, and Word export options for any report type
 * 
 * @param {string} reportType - The report type ID (e.g., 'burnout', 'compliance', 'clinical')
 * @param {string} token - Auth token
 * @param {string} variant - Button variant (default, outline, ghost)
 * @param {string} size - Button size (default, sm, lg)
 */
export function ExportDropdown({ reportType, token, variant = "outline", size = "sm" }) {
  const [downloading, setDownloading] = useState(null);

  const downloadFile = async (format) => {
    setDownloading(format);
    try {
      const endpoint = `/api/reports/export/${format}/${reportType}`;
      const response = await fetch(`${API_URL}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const extensions = { csv: 'csv', pdf: 'pdf', word: 'docx' };
        const filename = `${reportType}_report_${new Date().toISOString().split('T')[0]}.${extensions[format]}`;
        
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();

        toast.success(`Downloaded ${filename}`);
      } else {
        toast.error('Export failed');
      }
    } catch (error) {
      toast.error('Error downloading file');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant={variant} size={size} data-testid={`export-${reportType}-btn`}>
          {downloading ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Download className="h-4 w-4 mr-2" />
          )}
          Export
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem 
          onClick={() => downloadFile('csv')}
          disabled={downloading === 'csv'}
          className="cursor-pointer"
        >
          <FileSpreadsheet className="h-4 w-4 mr-2 text-green-600" />
          Export as CSV
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => downloadFile('pdf')}
          disabled={downloading === 'pdf'}
          className="cursor-pointer"
        >
          <FileText className="h-4 w-4 mr-2 text-red-600" />
          Export as PDF
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => downloadFile('word')}
          disabled={downloading === 'word'}
          className="cursor-pointer"
        >
          <File className="h-4 w-4 mr-2 text-blue-600" />
          Export as Word
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default ExportDropdown;
